// src/App.js
import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppProvider, useApp } from './context/AppContext';
import { openReader } from './utils/openReader';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import NotebooksPage from './pages/NotebooksPage';
import CompilationsPage from './pages/CompilationsPage';
import ArchivePage from './pages/ArchivePage';
import LexAssistPage from './pages/LexAssistPage';
import PricingPage from './pages/PricingPage';
import AccountPage from './pages/AccountPage';
import BookmarksPage from './pages/BookmarksPage';
import ReaderPage from './pages/ReaderPage';
import RegisterModal from './components/RegisterModal';
import { booksAPI } from './services/api';
import { Modal } from './components/ui';
import { decrypt } from './utils/security';

const Loader = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#F9EDEC,#EEF2F8,#EBF4F0)' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,.8)', border: '2px solid #E8B4B1', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontFamily: 'Playfair Display,serif', fontSize: 18, fontWeight: 700, color: '#C97B77' }}>AIR</div>
      <div style={{ width: 28, height: 28, border: '2px solid #E8B4B1', borderTopColor: '#C97B77', borderRadius: '50%', animation: 'spin .8s linear infinite', margin: '0 auto' }} />
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  </div>
);

const AppInner = () => {
  const { isLoggedIn, extLoading, getToken, needsProfileSetup, setNeedsProfileSetup } = useApp();
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  const [currentPage, setCurrentPage] = useState('home');
  const [selectedBook, setSelectedBook] = useState(null);
  const [bookLoading, setBookLoading] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('Subscription Required');

  useEffect(() => {
    const handleKeyDown = (e) => { if ((e.ctrlKey || e.metaKey) && ['s', 'p', 'S', 'P'].includes(e.key)) e.preventDefault(); };
    const handleCopy = (e) => e.preventDefault();
    const handleContextMenu = (e) => e.preventDefault();
    // PERF FIX: this used to be wrapped in setInterval(..., 1000), which re-added
    // these three window listeners every second for as long as the tab was open,
    // WITHOUT ever removing the previous ones in between (cleanup only ran on
    // unmount, and this component effectively never unmounts during a session).
    // Every keystroke, copy, and right-click was firing hundreds/thousands of
    // duplicate handlers the longer a reading session ran — a compounding
    // main-thread tax on top of the chapter-decrypt cost. Registering once here
    // achieves the same "block save/print/copy/right-click" behavior with zero
    // ongoing overhead.
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('copy', handleCopy);
    window.addEventListener('contextmenu', handleContextMenu);
    const params = new URLSearchParams(window.location.search);
    const bookId = params.get('book');
    if (bookId && isLoggedIn) {
      (async () => {
        setBookLoading(true);
        try {
          const token = await getToken();
          const res = await booksAPI.getBook(token, bookId);
          setSelectedBook(res.data); setCurrentPage('reader');
        } catch (err) {
          const isSub = err.message.toLowerCase().includes('subscription') || err.message.toLowerCase().includes('acess');
          setModalTitle(isSub ? 'Subscription Required' : 'Access Restricted');
          setModalMessage(err.message || 'You do not have access to this book.');
          setShowSubModal(true);
          const url = new URL(window.location.href);
          url.searchParams.delete('book'); url.searchParams.delete('chapter');
          window.history.replaceState({}, '', url);
          setCurrentPage('home');
        } finally { setBookLoading(false); }
      })();
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('copy', handleCopy);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [getToken, isLoggedIn]);

  if (isLoading || extLoading || bookLoading) return <Loader />;

  if (!isLoggedIn) {
    return <LoginPage onLogin={() => loginWithRedirect({ authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE } })} onExternalSuccess={() => window.location.reload()} />;
  }

  const handleNav = (page) => setCurrentPage(page);
  const handleOpenBook = async (book, chapterId = null, highlightText = null, scrollTop = 0) => {
    if (!book) return;
    const bookId = book._id || book.id;

    // Pre-open a blank tab inside the synchronous user click event to bypass Safari's popup blocker
    let newTab = null;
    try {
      newTab = window.open('', `air_reader_${bookId}`);
      if (newTab) {
        newTab.document.write('<html><head><title>Loading...</title></head><body style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;color:#888;background-color:#FDFAF7;"><div>Loading Reader...</div></body></html>');
      }
    } catch (e) {
      console.warn("Failed to pre-open tab:", e);
    }

    try {
      setBookLoading(true);
      const token = await getToken();
      const res = await booksAPI.getBook(token, bookId);
      const fullBook = res.data || book;

      const isAbsolute = (
        fullBook?.html_type === 'absolute_html' ||
        fullBook?.conversion_type === 'absolute_html' ||
        (fullBook?.is_external && !fullBook?.pdfstudio)
      );

      // Construct the URL directly
      const plainBookName = decrypt(fullBook.book_name || fullBook.book_title || '');
      let url;
      if (isAbsolute) {
        url = `${window.location.origin}/absolute_reader.html?book=${bookId}&name=${encodeURIComponent(plainBookName)}`;
        try {
          const activeToken = token || localStorage.getItem('air_access_token') || localStorage.getItem('air_ext_auth0_token') || window.__AIR_ACCESS_TOKEN__ || '';
          if (activeToken) url += `&token=${encodeURIComponent(activeToken)}`;
        } catch {}
      } else {
        url = `${window.location.origin}/?book=${bookId}`;
        if (chapterId) url += `&chapter=${chapterId}`;
        if (highlightText) url += `&highlight=${encodeURIComponent(highlightText)}`;
        if (scrollTop) url += `&scrollTop=${scrollTop}`;
        try {
          const extToken = localStorage.getItem('air_ext_auth0_token');
          if (extToken) url += `&ext_jwt=${encodeURIComponent(extToken)}`;
        } catch { }
      }

      if (newTab) {
        newTab.location.href = url;
      } else {
        window.open(url, `air_reader_${bookId}`, 'noopener');
      }
    } catch (err) {
      if (newTab) {
        newTab.close();
      }
      const isSub = err.message.toLowerCase().includes('subscription') || err.message.toLowerCase().includes('acess');
      setModalTitle(isSub ? 'Subscription Required' : 'Access Restricted');
      setModalMessage(err.message || 'You do not have access to this book.');
      setShowSubModal(true);
    } finally { setBookLoading(false); }
  };

  const pages = {
    home: <HomePage onNav={handleNav} onOpenBook={handleOpenBook} />,
    search: <SearchPage onNav={handleNav} onOpenBook={handleOpenBook} />,
    notebooks: <NotebooksPage onNav={handleNav} onOpenBook={handleOpenBook} />,
    compilations: <CompilationsPage onNav={handleNav} onOpenBook={handleOpenBook} />,
    archive: <ArchivePage onNav={handleNav} onOpenBook={handleOpenBook} />,
    lexassist: <LexAssistPage onNav={handleNav} />,
    pricing: <PricingPage onNav={handleNav} />,
    account: <AccountPage onNav={handleNav} />,
    bookmarks: <BookmarksPage onOpenBook={handleOpenBook} />,
    reader: <ReaderPage book={selectedBook} onBack={() => handleNav('home')} onOpenBook={handleOpenBook} />,
  };

  const currentView = (currentPage === 'reader' && selectedBook)
    ? pages.reader
    : <Layout currentPage={currentPage} onNav={handleNav}>{pages[currentPage] || pages.home}</Layout>;

  return (
    <>
      {currentView}

      {needsProfileSetup && (
        <RegisterModal onComplete={() => setNeedsProfileSetup(false)} />
      )}

      <Modal isOpen={showSubModal} onClose={() => setShowSubModal(false)} title={modalTitle} icon="🔐" actionLabel="Explore Plans" onAction={() => { setShowSubModal(false); handleNav('pricing'); }}>
        <div style={{ textAlign: 'center', padding: '10px 0' }}>
          <p className="mb-4 font-bold text-lg" style={{ fontFamily: 'Nunito,sans-serif', color: '#B04C49' }}>You don't have access to this book.</p>
          <div className="bg-[#F9EDEC] p-4 rounded-xl text-[#C97B77] text-xs border border-[#C97B77]/20" style={{ fontFamily: 'Nunito,sans-serif', lineHeight: 1.5 }}>
            {modalMessage || 'Your current subscription plan does not include this title. Please upgrade to continue reading.'}
          </div>
        </div>
      </Modal>
    </>
  );
};

const App = () => (
  <AppProvider>
    <AppInner />
  </AppProvider>
);

export default App;